import type { ToolRunner } from "@agent-tradekit/core";
import { output, outputLine, printJson, printKv, printTable } from "../formatter.js";
import { resolveIndicatorCode, getDefaultIndicatorParams, KNOWN_INDICATORS } from "@agent-tradekit/core";

/** Verbatim safety-net hint (spec §8.2) shown when the indicator render loop prints nothing. */
const NO_INDICATOR_VALUES_HINT =
  "No indicator values returned. This indicator may require a period — try --params (e.g. --params 14).";

function getData(result: unknown): unknown {
  return (result as Record<string, unknown>).data;
}

export async function cmdMarketInstruments(
  run: ToolRunner,
  opts: { instType: string; instId?: string; uly?: string; instFamily?: string; seriesId?: string; json: boolean; demo?: boolean },
): Promise<void> {
  const result = await run("market_get_instruments", { instType: opts.instType, instId: opts.instId, uly: opts.uly, instFamily: opts.instFamily, seriesId: opts.seriesId, demo: opts.demo ?? false });
  const items = getData(result) as Record<string, unknown>[];
  if (opts.json) return printJson(items);
  printTable(
    (items ?? []).slice(0, 50).map((t) => ({
      instId: t["instId"],
      ctVal: t["ctVal"],
      lotSz: t["lotSz"],
      minSz: t["minSz"],
      tickSz: t["tickSz"],
      state: t["state"],
    })),
  );
}

export async function cmdMarketFundingRate(
  run: ToolRunner,
  instId: string,
  opts: { history: boolean; limit?: number; json: boolean; demo?: boolean },
): Promise<void> {
  const result = await run("market_get_funding_rate", { instId, history: opts.history, limit: opts.limit, demo: opts.demo ?? false });
  const items = getData(result) as Record<string, unknown>[];
  if (opts.json) return printJson(items);
  if (opts.history) {
    printTable(
      (items ?? []).map((r) => ({
        instId: r["instId"],
        fundingRate: r["fundingRate"],
        realizedRate: r["realizedRate"],
        fundingTime: new Date(Number(r["fundingTime"])).toLocaleString(),
      })),
    );
  } else {
    const r = items?.[0];
    if (!r) { outputLine("No data"); return; }
    printKv({
      instId: r["instId"],
      fundingRate: r["fundingRate"],
      nextFundingRate: r["nextFundingRate"],
      fundingTime: new Date(Number(r["fundingTime"])).toLocaleString(),
      nextFundingTime: new Date(Number(r["nextFundingTime"])).toLocaleString(),
    });
  }
}

export async function cmdMarketMarkPrice(
  run: ToolRunner,
  opts: { instType: string; instId?: string; json: boolean; demo?: boolean },
): Promise<void> {
  const result = await run("market_get_mark_price", { instType: opts.instType, instId: opts.instId, demo: opts.demo ?? false });
  const items = getData(result) as Record<string, unknown>[];
  if (opts.json) return printJson(items);
  printTable(
    (items ?? []).map((r) => ({
      instId: r["instId"],
      instType: r["instType"],
      markPx: r["markPx"],
      ts: new Date(Number(r["ts"])).toLocaleString(),
    })),
  );
}

export async function cmdMarketTrades(
  run: ToolRunner,
  instId: string,
  opts: { limit?: number; json: boolean; demo?: boolean },
): Promise<void> {
  const result = await run("market_get_trades", { instId, limit: opts.limit, demo: opts.demo ?? false });
  const items = getData(result) as Record<string, unknown>[];
  if (opts.json) return printJson(items);
  printTable(
    (items ?? []).map((t) => ({
      tradeId: t["tradeId"],
      px: t["px"],
      sz: t["sz"],
      side: t["side"],
      ts: new Date(Number(t["ts"])).toLocaleString(),
    })),
  );
}

export async function cmdMarketIndexTicker(
  run: ToolRunner,
  opts: { instId?: string; quoteCcy?: string; json: boolean; demo?: boolean },
): Promise<void> {
  const result = await run("market_get_index_ticker", { instId: opts.instId, quoteCcy: opts.quoteCcy, demo: opts.demo ?? false });
  const items = getData(result) as Record<string, unknown>[];
  if (opts.json) return printJson(items);
  printTable(
    (items ?? []).map((t) => ({
      instId: t["instId"],
      idxPx: t["idxPx"],
      high24h: t["high24h"],
      low24h: t["low24h"],
      ts: new Date(Number(t["ts"])).toLocaleString(),
    })),
  );
}

export async function cmdMarketIndexCandles(
  run: ToolRunner,
  instId: string,
  opts: { bar?: string; limit?: number; history: boolean; json: boolean; demo?: boolean },
): Promise<void> {
  const result = await run("market_get_index_candles", { instId, bar: opts.bar, limit: opts.limit, history: opts.history, demo: opts.demo ?? false });
  const candles = getData(result) as string[][];
  if (opts.json) return printJson(candles);
  printTable(
    (candles ?? []).map(([ts, o, h, l, c]) => ({
      time: new Date(Number(ts)).toLocaleString(),
      open: o, high: h, low: l, close: c,
    })),
  );
}

export async function cmdMarketPriceLimit(
  run: ToolRunner,
  instId: string,
  json: boolean,
  demo?: boolean,
): Promise<void> {
  const result = await run("market_get_price_limit", { instId, demo: demo ?? false });
  const items = getData(result) as Record<string, unknown>[];
  if (json) return printJson(items);
  const r = items?.[0];
  if (!r) { outputLine("No data"); return; }
  printKv({
    instId: r["instId"],
    buyLmt: r["buyLmt"],
    sellLmt: r["sellLmt"],
    ts: new Date(Number(r["ts"])).toLocaleString(),
  });
}

export async function cmdMarketOpenInterest(
  run: ToolRunner,
  opts: { instType: string; instId?: string; json: boolean; demo?: boolean },
): Promise<void> {
  const result = await run("market_get_open_interest", { instType: opts.instType, instId: opts.instId, demo: opts.demo ?? false });
  const items = getData(result) as Record<string, unknown>[];
  if (opts.json) return printJson(items);
  printTable(
    (items ?? []).map((r) => ({
      instId: r["instId"],
      oi: r["oi"],
      oiCcy: r["oiCcy"],
      ts: new Date(Number(r["ts"])).toLocaleString(),
    })),
  );
}

export async function cmdMarketTicker(
  run: ToolRunner,
  instId: string,
  json: boolean,
  demo?: boolean,
): Promise<void> {
  const result = await run("market_get_ticker", { instId, demo: demo ?? false });
  const items = getData(result) as Record<string, unknown>[];
  if (json) return printJson(items);
  if (!items?.length) { outputLine("No data"); return; }
  const t = items[0];
  printKv({
    instId: t["instId"],
    last: t["last"],
    "24h open": t["open24h"],
    "24h high": t["high24h"],
    "24h low": t["low24h"],
    "24h vol": t["vol24h"],
    "24h change %": (() => {
      const last = Number(t["last"]);
      const open24h = Number(t["open24h"]);
      return open24h !== 0 ? (((last - open24h) / open24h) * 100).toFixed(2) + "%" : "N/A";
    })(),
    time: new Date(Number(t["ts"])).toLocaleString(),
  });
}

export async function cmdMarketTickers(
  run: ToolRunner,
  instType: string,
  json: boolean,
  demo?: boolean,
): Promise<void> {
  const result = await run("market_get_tickers", { instType, demo: demo ?? false });
  const items = getData(result) as Record<string, unknown>[];
  if (json) return printJson(items);
  printTable(
    (items ?? []).map((t) => ({
      instId: t["instId"],
      last: t["last"],
      "24h high": t["high24h"],
      "24h low": t["low24h"],
      "24h vol": t["vol24h"],
    })),
  );
}

export async function cmdMarketOrderbook(
  run: ToolRunner,
  instId: string,
  sz: number | undefined,
  json: boolean,
  demo?: boolean,
): Promise<void> {
  const result = await run("market_get_orderbook", { instId, sz, demo: demo ?? false });
  const data = getData(result);
  if (json) return printJson(data);
  const book = (data as Record<string, unknown>[])[0];
  if (!book) { outputLine("No data"); return; }
  const asks = (book["asks"] as string[][]).slice(0, 5);
  const bids = (book["bids"] as string[][]).slice(0, 5);
  outputLine("Asks (price / size):");
  asks.reverse();
  for (const [p, s] of asks) outputLine(`  ${p.padStart(16)}  ${s}`);
  outputLine("Bids (price / size):");
  for (const [p, s] of bids) outputLine(`  ${p.padStart(16)}  ${s}`);
}

export async function cmdMarketCandles(
  run: ToolRunner,
  instId: string,
  opts: { bar?: string; limit?: number; after?: string; before?: string; json: boolean; demo?: boolean },
): Promise<void> {
  const result = await run("market_get_candles", { instId, bar: opts.bar, limit: opts.limit, after: opts.after, before: opts.before, demo: opts.demo ?? false });
  const candles = getData(result) as string[][];
  if (opts.json) return printJson(candles);
  printTable(
    (candles ?? []).map(([ts, o, h, l, c, vol]) => ({
      time: new Date(Number(ts)).toLocaleString(),
      open: o, high: h, low: l, close: c, vol,
    })),
  );
}

export function cmdMarketIndicatorList(json: boolean): void {
  if (json) return printJson(KNOWN_INDICATORS);
  printTable(KNOWN_INDICATORS.map(({ name, description }) => ({ name, description })));
}

export async function cmdMarketIndicator(
  run: ToolRunner,
  indicator: string,
  instId: string,
  opts: {
    bar?: string;
    params?: string;
    list?: boolean;
    limit?: number;
    backtestTime?: number;
    json: boolean;
  },
): Promise<void> {
  const params = opts.params
    ? opts.params.split(",").map((p) => Number(p.trim())).filter((n) => !Number.isNaN(n))
    : undefined;

  // Plan B (CLI-layer only): when --params is omitted, substitute the core
  // default paramList for period-based indicators so the server returns values.
  // Explicit --params always wins. MCP raw-data path is intentionally unchanged.
  const explicit = params && params.length > 0 ? params : undefined;
  const effectiveParams = explicit ?? getDefaultIndicatorParams(indicator);

  const result = await run("market_get_indicator", {
    instId,
    indicator,
    bar: opts.bar,
    params: effectiveParams,
    returnList: opts.list ?? false,
    limit: opts.limit,
    backtestTime: opts.backtestTime,
  });

  // Response shape: data = Array<{ data: [{instId, timeframes}], mode, summary, timestamp }>
  const outerArray = getData(result) as Record<string, unknown>[];
  if (opts.json) return printJson(outerArray);

  if (!outerArray?.length) { outputLine("No data"); return; }

  const apiCode = resolveIndicatorCode(indicator);
  const response = outerArray[0];
  const innerArray = response["data"] as Record<string, unknown>[] | undefined;
  const instData = innerArray?.[0];
  const timeframes = instData?.["timeframes"] as Record<string, unknown> | undefined;

  if (!timeframes) {
    output(JSON.stringify(outerArray, null, 2) + "\n");
    return;
  }

  // Plan A (safety net): track whether anything rendered; if not, emit a hint
  // so the command is never silent (non-period indicators or empty results).
  let printed = false;
  for (const [tf, tfData] of Object.entries(timeframes)) {
    const indicators = (tfData as Record<string, unknown>)?.["indicators"] as Record<string, unknown> | undefined;
    const values = indicators?.[apiCode] as Record<string, unknown>[] | undefined;
    if (!values?.length) continue;

    printed = true;
    output(`${instId} | ${apiCode} | ${tf}\n`);
    output("-".repeat(40) + "\n");

    if (opts.list) {
      const tableRows = values.map((entry) => ({
        ts: new Date(Number(entry["ts"])).toLocaleString(),
        ...entry["values"] as Record<string, unknown>,
      }));
      printTable(tableRows);
    } else {
      const latest = values[0];
      printKv({
        ts: new Date(Number(latest["ts"])).toLocaleString(),
        ...latest["values"] as Record<string, unknown>,
      });
    }
  }

  if (!printed) outputLine(NO_INDICATOR_VALUES_HINT);
}

export async function cmdMarketInstrumentsByCategory(
  run: ToolRunner,
  opts: { instCategory: string; instType?: string; instId?: string; json: boolean; demo?: boolean },
): Promise<void> {
  const result = await run("market_get_instruments_by_category", {
    instCategory: opts.instCategory,
    instType: opts.instType,
    instId: opts.instId,
    demo: opts.demo ?? false,
  });
  const items = getData(result) as Record<string, unknown>[];
  if (opts.json) return printJson(items);
  const CATEGORY_LABELS: Record<string, string> = {
    "3": "Stock tokens",
    "4": "Metals",
    "5": "Commodities",
    "6": "Forex",
    "7": "Bonds",
  };
  const label = CATEGORY_LABELS[opts.instCategory] ?? opts.instCategory;
  process.stdout.write(`instCategory=${opts.instCategory} (${label}) - ${items?.length ?? 0} instruments\n\n`);
  printTable(
    (items ?? []).slice(0, 50).map((t) => ({
      instId: t["instId"],
      instCategory: t["instCategory"],
      ctVal: t["ctVal"],
      lotSz: t["lotSz"],
      minSz: t["minSz"],
      tickSz: t["tickSz"],
      state: t["state"],
    })),
  );
}

export async function cmdMarketStockTokens(
  run: ToolRunner,
  opts: { instType?: string; instId?: string; json: boolean; demo?: boolean },
): Promise<void> {
  const result = await run("market_get_stock_tokens", { instType: opts.instType, instId: opts.instId, demo: opts.demo ?? false });
  const items = getData(result) as Record<string, unknown>[];
  if (opts.json) return printJson(items);
  printTable(
    (items ?? []).slice(0, 50).map((t) => ({
      instId: t["instId"],
      instCategory: t["instCategory"],
      ctVal: t["ctVal"],
      lotSz: t["lotSz"],
      minSz: t["minSz"],
      tickSz: t["tickSz"],
      state: t["state"],
    })),
  );
}

export async function cmdMarketFilter(
  run: ToolRunner,
  opts: {
    instType: string;
    baseCcy?: string;
    quoteCcy?: string;
    settleCcy?: string;
    instFamily?: string;
    ctType?: string;
    minLast?: string;
    maxLast?: string;
    minChg24hPct?: string;
    maxChg24hPct?: string;
    minMarketCapUsd?: string;
    maxMarketCapUsd?: string;
    minVolUsd24h?: string;
    maxVolUsd24h?: string;
    minFundingRate?: string;
    maxFundingRate?: string;
    minOiUsd?: string;
    maxOiUsd?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    json: boolean;
  },
): Promise<void> {
  const result = await run("market_filter", {
    instType:        opts.instType,
    baseCcy:         opts.baseCcy,
    quoteCcy:        opts.quoteCcy,
    settleCcy:       opts.settleCcy,
    instFamily:      opts.instFamily,
    ctType:          opts.ctType,
    minLast:         opts.minLast,
    maxLast:         opts.maxLast,
    minChg24hPct:    opts.minChg24hPct,
    maxChg24hPct:    opts.maxChg24hPct,
    minMarketCapUsd: opts.minMarketCapUsd,
    maxMarketCapUsd: opts.maxMarketCapUsd,
    minVolUsd24h:    opts.minVolUsd24h,
    maxVolUsd24h:    opts.maxVolUsd24h,
    minFundingRate:  opts.minFundingRate,
    maxFundingRate:  opts.maxFundingRate,
    minOiUsd:        opts.minOiUsd,
    maxOiUsd:        opts.maxOiUsd,
    sortBy:          opts.sortBy,
    sortOrder:       opts.sortOrder,
    limit:           opts.limit,
  });
  const raw = getData(result);
  if (opts.json) return printJson(raw);
  const data = (Array.isArray(raw) ? raw[0] : raw) as Record<string, unknown> | null;
  const rows = (data?.["rows"] ?? []) as Record<string, unknown>[];
  const total = data?.["total"] ?? rows.length;
  outputLine(`Total: ${total}`);
  if (!rows.length) { outputLine("No results"); return; }
  const isSwap = opts.instType?.toUpperCase() === "SWAP";
  printTable(
    rows.map((r) => {
      const base: Record<string, unknown> = {
        rank:      r["rank"],
        instId:    r["instId"],
        last:      r["last"],
        "chg24h%": r["chg24hPct"],
        volUsd24h: r["volUsd24h"],
        oiUsd:     r["oiUsd"],
      };
      if (isSwap) {
        base["fundingRate"] = r["fundingRate"];
      }
      base["sortVal"] = r["sortVal"];
      return base;
    }),
  );
}

export async function cmdMarketOiHistory(
  run: ToolRunner,
  instId: string,
  opts: {
    bar?: string;
    limit?: number;
    ts?: number;
    json: boolean;
  },
): Promise<void> {
  const result = await run("market_get_oi_history", {
    instId,
    bar:   opts.bar,
    limit: opts.limit,
    ts:    opts.ts,
  });
  const data = getData(result);
  if (opts.json) return printJson(data);
  const entry = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null | undefined;
  const rows = (entry?.["rows"] ?? []) as Record<string, unknown>[];
  if (!rows.length) { outputLine("No OI data"); return; }
  outputLine(`${entry?.["instId"] ?? instId}  bar=${entry?.["bar"] ?? opts.bar ?? "1H"}`);
  printTable(
    rows.map((r) => ({
      ts:          new Date(Number(r["ts"])).toLocaleString(),
      oiUsd:       r["oiUsd"] != null ? String(r["oiUsd"]) : "-",
      oiDeltaUsd:  r["oiDeltaUsd"] != null ? String(r["oiDeltaUsd"]) : "-",
      "deltaOi%":  r["oiDeltaPct"] != null ? String(r["oiDeltaPct"]) : "-",
      oiCont:      r["oiCont"] != null ? String(r["oiCont"]) : "-",
    })),
  );
}

export async function cmdMarketOiChangeFilter(
  run: ToolRunner,
  opts: {
    instType: string;
    bar?: string;
    minOiUsd?: string;
    minVolUsd24h?: string;
    minAbsOiDeltaPct?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    json: boolean;
  },
): Promise<void> {
  const result = await run("market_filter_oi_change", {
    instType:         opts.instType,
    bar:              opts.bar,
    minOiUsd:         opts.minOiUsd,
    minVolUsd24h:     opts.minVolUsd24h,
    minAbsOiDeltaPct: opts.minAbsOiDeltaPct,
    sortBy:           opts.sortBy,
    sortOrder:        opts.sortOrder,
    limit:            opts.limit,
  });
  const rows = (getData(result) ?? []) as Record<string, unknown>[];
  if (opts.json) return printJson(rows);
  if (!rows.length) { outputLine("No results"); return; }
  printTable(
    rows.map((r) => ({
      rank:        r["rank"],
      instId:      r["instId"],
      last:        r["last"],
      oiUsd:       r["oiUsd"],
      "deltaOi%":  r["oiDeltaPct"],
      "pxChg%":    r["pxChgPct"],
      volUsd24h:   r["volUsd24h"],
      fundingRate: r["fundingRate"],
    })),
  );
}

export async function cmdMarketPairSpread(
  run: ToolRunner,
  instIdA: string,
  instIdB: string,
  opts: { bar?: string; window?: string; backtestTime?: number; json: boolean },
): Promise<void> {
  const result = await run("market_get_pair_spread", {
    instIdA,
    instIdB,
    bar: opts.bar,
    window: opts.window,
    backtestTime: opts.backtestTime,
  });
  const data = getData(result) as Record<string, unknown> | undefined;
  if (opts.json) return printJson(data);

  const bar = (data?.["bar"] ?? "15m") as string;
  const win = (data?.["window"] ?? "1W") as string;
  const mode = (data?.["mode"] ?? "live") as string;
  outputLine(`${instIdA} / ${instIdB}  bar=${bar} window=${win}  mode=${mode}`);

  const rt = data?.["realtime"] as Record<string, unknown> | null;
  if (rt) {
    outputLine(`realtime  lastA=${rt["lastPriceA"]}  lastB=${rt["lastPriceB"]}  abs=${rt["spreadAbs"]}  ratio=${rt["spreadRatio"]}`);
  }

  const stats = data?.["statistics"] as Record<string, unknown> | undefined;
  if (stats) {
    const abs = stats["absolute"] as Record<string, unknown>;
    const ratio = stats["ratio"] as Record<string, unknown>;
    const meta = data?.["meta"] as Record<string, unknown>;
    outputLine(`samples  count=${meta?.["alignedBars"] ?? "?"}  start=${stats?.["windowStartTs"] ?? "?"}  end=${stats?.["windowEndTs"] ?? "?"}`);
    printTable([
      { stat: "mean", absolute: abs?.["mean"], ratio: ratio?.["mean"] },
      { stat: "stdDev", absolute: abs?.["stdDev"], ratio: ratio?.["stdDev"] },
      { stat: "median", absolute: abs?.["median"], ratio: ratio?.["median"] },
      { stat: "min", absolute: abs?.["min"], ratio: ratio?.["min"] },
      { stat: "max", absolute: abs?.["max"], ratio: ratio?.["max"] },
    ]);
    if (meta) {
      outputLine(`meta  requested=${meta["requestedBars"]}  aligned=${meta["alignedBars"]}  dropped=${meta["droppedBars"]}  truncated=${meta["truncated"]}`);
    }
  }
}
